<script setup lang="ts">
import { ShopCore } from "@/abilities/shop/types/shop-core";
import { AccessFeature } from "@/shared/access-control/access-control";
import { useHasAccess } from "@/shared/access-control/composables/use-has-access";
import { get } from "@vueuse/core";
import { useQuasar } from "quasar";
import { computed, toRefs } from "vue";
import { useShopStatusStore } from "../../store/shop-status.store";
import NumberInput from "../number-input.vue";
import ShowPriceWithCurrency from "../show-price-with-currency.vue";

// props
const props = defineProps<{
    addToBasketError: boolean;
    infoItem: ShopCore.WidgetItem | null;
    priceRequestError: boolean;
    moreSelectedThanAvailable: boolean;
    quantitySelected: number;
}>();

const { quantitySelected } = toRefs(props);

// emits
const emit = defineEmits<{
    (e: "quantity-change", value: number): void;
    (e: "add-to-basket"): void;
    (e: "request-price"): void;
}>();

// external logic / functions
const $q = useQuasar();
const store = useShopStatusStore();

//TODO: QIT-6160 Remove temp feature flag
//temporary fresenius fix: hide controls
const { hasAccess: hideFresenius } = useHasAccess({ featureID: AccessFeature.fresenius_shop_gui });

// computed state
const totalPrice = computed(() => {
    if (props.infoItem === null || props.infoItem.price.amount === null) {
        return null;
    } else if (quantitySelected.value === undefined) {
        return 0;
    } else {
        return props.infoItem.price.amount * get(quantitySelected);
    }
});

// quantity change handler
function quantityInputChange(val: number) {
    emit("quantity-change", val);
}

const checkoutEnabled = computed(() => {
    if (store.isShopLight) {
        return props.infoItem?.stock.available !== ShopCore.StockStatus.UNAVAILABLE && quantitySelected.value > 0;
    }
    if (props.infoItem === null) {
        return false;
    }
    /*
     * An adapter whose shop answers the orderability question outright wins over
     * the stock rule: the generic shop reports articles that are in stock but not
     * orderable, and articles that are orderable with no stock figure at all
     * (QIT-7074). Adapters that report nothing leave `orderable` undefined and
     * keep the stock rule they have always used.
     */
    return (
        store.isShopOnline &&
        quantitySelected.value > 0 &&
        (props.infoItem.orderable ?? props.infoItem.stock.quantity > 0)
    );
});

const isNotValidPrice = computed(
    () =>
        props.infoItem?.price.amount === null ||
        props.infoItem?.price.amount === undefined ||
        props.infoItem?.price.currency === undefined ||
        props.infoItem?.price.currencySign === undefined
);
</script>
<template>
    <div class="shop-controls">
        <div class="space-between-flex">
            <div class="quantity-label">{{ $t("shop.quantity") }}</div>
            <ShowPriceWithCurrency
                v-show="!hideFresenius"
                v-if="!infoItem?.price.onRequestOnly"
                class="black-bold-text"
                :pre-label="$t('shop.Total')"
                :price="totalPrice"
                :currency="infoItem?.price.currencySign"
                :convert-to-decimal="store.isShopLight"
                :use-currency-symbol="store.isShopLight"
            />
        </div>
        <div class="row no-wrap">
            <NumberInput
                class="amount-input"
                :initial-value="quantitySelected"
                :min="0"
                :max="store.isShopOnline === false ? 0 : Infinity"
                @on-change="quantityInputChange"
                validate-on-edit
                :disabled="infoItem?.price.onRequestOnly || store.isShopOnline === false || infoItem === null"
                :warning-highlight="moreSelectedThanAvailable"
            />
            <q-btn
                v-if="!infoItem?.price.onRequestOnly"
                no-caps
                :label="store.isShopLight && isNotValidPrice ? $t('shop.Request price') : $t('shop.Add to cart')"
                color="primary"
                :icon="$q.screen.gt.md ? 'fa-regular fa-cart-plus text-on-primary-color' : undefined"
                class="checkout-btn col"
                data-qs="shop-widget-add-to-cart-button"
                @click="$emit('add-to-basket')"
                :disable="!checkoutEnabled || addToBasketError"
            >
                <q-tooltip self="top right" v-if="infoItem === null">{{ $t("shop.Article not found") }}</q-tooltip>
                <q-tooltip self="top right" v-if="addToBasketError">{{
                    $t("shop.Article could not be added to the cart. Please try again.")
                }}</q-tooltip>
            </q-btn>
            <q-btn
                v-else
                no-caps
                :label="$t('shop.Request price')"
                color="primary"
                icon="fa-regular fa-message-question text-on-primary-color"
                class="checkout-btn col"
                data-qs="shop-widget-request-price-button"
                @click="$emit('request-price')"
                :disable="store.isShopOnline === false || priceRequestError || infoItem?.orderable === false"
            />
        </div>
    </div>
</template>
<style lang="scss" scoped>
.shop-controls {
    gap: $spacing-s;
    display: flex;
    flex-direction: column;
}

.space-between-flex {
    display: flex;
    flex-direction: row;
    justify-content: space-between;
    gap: 24px;
}

.amount-input {
    margin-right: $spacing-l;
    max-width: 90px;
}

.quantity-label {
    font-size: 12px;
    color: $grey-30;
}

.checkout-btn {
    min-width: 130px;
}

.black-bold-text {
    font-weight: $semi-bold;
    color: $text-color-default;
}
</style>
